'use client';

import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import {
    AllDisplayEvTypes,
    DisplayToEvTypes,
    EvToDisplayTypes,
    EvType,
} from '@/lib/actions/usage/constants';
import {
    addCleanHeatAndAcAtom,
    addEvExpandAtom,
    addHeatPumpWaterHeaterAtom,
    addHvacUseAtom,
    addInductionStoveAtom,
    addPeopleAtom,
    changeAddPeopleAtom,
    electricHeatAtom,
    Ev,
    evsAtom,
    hasAcAtom,
    hasElectricStoveAtom,
    hasElectricWaterHeaterAtom,
    hasHeatPumpAtom,
    hhSizeAtom,
    numEvsAtom,
} from '@/ui/atoms/form-atoms';
import { NoSSR } from '@/ui/utils/NoSSR';
import { Select } from '@mantine/core';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo } from 'react';
import {
    BooleanForm,
    ExpandableForm,
    FormInputs,
    InputStyleClassName,
    MiddleLine,
    NumberInput,
} from './building-blocks';
import styles from './CustomerInput.module.css';
import { LeftDrawer } from './LeftDrawer';

export const CustomerInput: React.FC = () => {
    return (
        <NoSSR>
            <LeftDrawer />
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '30px',
                    maxWidth: '1000px',
                    paddingLeft: '60px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginTop: '100px',
                }}
            >
                <AddCleanHeatAndAcFormBox />
                <AddHvacUseFormBox />
                <AddHeatPumpWaterHeaterFormBox />
                <AddEvFormBox />
                <AddInductionStoveFormBox />
                <AddPeopleFormBox />
            </div>
        </NoSSR>
    );
};

const HomeChgPctValues = [
    '',
    '100',
    '90',
    '80',
    '70',
    '60',
    '50',
    '40',
    '30',
    '20',
    '10',
    '0',
];

const AddEvFormBox: React.FC = () => {
    const [expand, setExpand] = useAtom(addEvExpandAtom);
    const [numEvs, setNumEvs] = useAtom(numEvsAtom);

    const [evs, setEvs] = useAtom(evsAtom);

    // Update num EVs.
    useEffect(() => {
        const num = numEvs ?? 0;
        if (num === evs.length) return;
        const newEvs = [];

        for (let i = 0; i < num; i++) {
            if (evs && evs.length > i) {
                newEvs.push(evs[i]);
            } else {
                // Set some default values.
                newEvs.push({
                    type: undefined,
                    milesPerYear: '15000',
                    homeChgPct: '80',
                });
            }
        }

        setEvs(newEvs);
    }, [evs, numEvs, setEvs]);

    const updateEv = useCallback(
        (fn: (evs: Ev[]) => void) => {
            const newEvs = cloneDeep(evs);
            fn(newEvs);
            setEvs(newEvs);
        },
        [evs, setEvs]
    );

    // Clear data on collapse.
    useEffect(() => {
        if (!expand) {
            setNumEvs(0);
        }
    }, [expand, setNumEvs]);

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addEvBox,
                numEvs === 2 && styles.twoEvs,
                expand && styles.expanded
            )}
        >
            <ExpandableForm
                expand={expand}
                showAddedLabel={numEvs > 0}
                setExpand={(b: boolean) => {
                    setExpand(b);
                    ssrTrack(b ? `clickAddEv` : `clickRemoveEv`);
                }}
                label="Adding an EV?"
            >
                <FormInputs expand={expand}>
                    <Select
                        className={InputStyleClassName}
                        checkIconPosition="right"
                        label="Number of EVs"
                        value={String(numEvs)}
                        onChange={(value) => setNumEvs(Number(value))}
                        data={['0', '1', '2']}
                    />

                    <div className={styles.evDetails}>
                        {evs.map((ev, index) => {
                            return (
                                <div className={styles.evDetail} key={index}>
                                    {numEvs > 1 && (
                                        <div className={styles.evNum}>
                                            <div>EV #{index + 1}</div>
                                            <MiddleLine />
                                        </div>
                                    )}

                                    <Select
                                        className={InputStyleClassName}
                                        checkIconPosition="right"
                                        label="Type of EV"
                                        value={
                                            ev.type && EvToDisplayTypes[ev.type]
                                        }
                                        onChange={(value) => {
                                            updateEv((evs) => {
                                                const v = (value ??
                                                    undefined) as
                                                    | EvType
                                                    | undefined;
                                                evs[index].type =
                                                    v && DisplayToEvTypes[v];
                                            });
                                        }}
                                        data={AllDisplayEvTypes}
                                    />

                                    <NumberInput
                                        label="Miles Driven per Year"
                                        value={ev.milesPerYear}
                                        setValue={(v) => {
                                            updateEv((evs) => {
                                                evs[index].milesPerYear = v;
                                            });
                                        }}
                                    />

                                    <Select
                                        className={InputStyleClassName}
                                        checkIconPosition="right"
                                        label="% Charging at Home"
                                        value={ev.homeChgPct}
                                        onChange={(value) => {
                                            updateEv((evs) => {
                                                evs[index].homeChgPct =
                                                    value ?? '';
                                            });
                                        }}
                                        data={HomeChgPctValues}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </FormInputs>
            </ExpandableForm>
        </div>
    );
};

const AddHvacUseFormBox: React.FC = () => {
    const [addHvacUse, setAddHvacUse] = useAtom(addHvacUseAtom);
    const [addCleanHeatAndAc] = useAtom(addCleanHeatAndAcAtom);
    const [electricHeat] = useAtom(electricHeatAtom);
    const [hasAc] = useAtom(hasAcAtom);
    const [hasHeatPump] = useAtom(hasHeatPumpAtom);

    // Determine if AddHvacUseFormBox should be shown

    const dontShowAddHvacUse =
        !hasAc && !electricHeat && !hasHeatPump && !addCleanHeatAndAc;
    const showAddHvacUse = !dontShowAddHvacUse;

    const tooltipText = addCleanHeatAndAc
        ? 'It increases usage for both heating and cooling.'
        : electricHeat && hasAc
          ? 'It increases usage for both heating and cooling.'
          : electricHeat
            ? 'It only increases usage for heating.'
            : hasAc
              ? 'It only increases usage for cooling.'
              : 'It has no effect since you do not have heat or AC.';

    useEffect(() => {
        if (!showAddHvacUse) {
            setAddHvacUse(false);
        }
    }, [showAddHvacUse, setAddHvacUse]);

    if (!showAddHvacUse) return null;

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addHvacUseBox,
                addHvacUse && styles.expanded
            )}
        >
            <BooleanForm
                value={addHvacUse}
                tooltipContent={tooltipText}
                setValue={(b: boolean) => {
                    setAddHvacUse(b);
                    ssrTrack(b ? 'clickAddHvacUse' : 'clickRemoveHvacUse');
                }}
                label={
                    <>
                        {addHvacUse
                            ? 'Added HVAC usage for more comfortable temperatures'
                            : 'Adding HVAC usage for more comfortable temperatures?'}
                    </>
                }
            />
        </div>
    );
};

const AddCleanHeatAndAcFormBox: React.FC = () => {
    const [hasAc] = useAtom(hasAcAtom);
    const [electricHeat] = useAtom(electricHeatAtom);
    const [hasHeatPump] = useAtom(hasHeatPumpAtom);
    const [addCleanHeatAndAc, setAddCleanHeatAndAc] = useAtom(
        addCleanHeatAndAcAtom
    );

    useEffect(() => {
        console.log({
            hasAc,
            electricHeat,
            hasHeatPump,
            addCleanHeatAndAc,
        });
    }, [hasAc, electricHeat, hasHeatPump, addCleanHeatAndAc]);

    /**
     * https://docs.google.com/document/d/117Bkthav-cmTuwCfCbRC6IxLdRWiQoJacPVN0Bl8-hk/edit?tab=t.0#heading=h.7xhscsxu54qq
     */
    const showAddCleanHeatAndAc = !electricHeat;

    useEffect(() => {
        if (!showAddCleanHeatAndAc) {
            setAddCleanHeatAndAc(false);
        }
    }, [showAddCleanHeatAndAc, setAddCleanHeatAndAc]);

    if (!showAddCleanHeatAndAc) return null;

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addCleanHeatAndAcBox,
                addCleanHeatAndAc && styles.expanded
            )}
        >
            <BooleanForm
                tooltipContent="Heat pumps provide both space heating and cooling using electricity, making them cleaner and more efficient than a gas furnace. They are perfect for reducing carbon footprint while keeping your home comfortable year-round!"
                value={addCleanHeatAndAc}
                setValue={(b: boolean) => {
                    setAddCleanHeatAndAc(b);
                    ssrTrack(
                        b
                            ? 'clickAddCleanHeatAndAc'
                            : 'clickRemoveCleanHeatAndAc'
                    );
                }}
                label={
                    addCleanHeatAndAc
                        ? 'Clean Heat & AC Added'
                        : 'Adding Clean Heat & AC?'
                }
            />
        </div>
    );
};

const AddHeatPumpWaterHeaterFormBox: React.FC = () => {
    const [addHeatPumpWaterHeater, setAddHeatPumpWaterHeater] = useAtom(
        addHeatPumpWaterHeaterAtom
    );
    const [hasElectricWaterHeater] = useAtom(hasElectricWaterHeaterAtom);

    const showAddHeatPumpWaterHeater = !hasElectricWaterHeater;

    useEffect(() => {
        if (!showAddHeatPumpWaterHeater) {
            setAddHeatPumpWaterHeater(false);
        }
    }, [showAddHeatPumpWaterHeater, setAddHeatPumpWaterHeater]);

    if (!showAddHeatPumpWaterHeater) return null;

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addHeatPumpWaterHeaterBox,
                addHeatPumpWaterHeater && styles.expanded
            )}
        >
            <BooleanForm
                tooltipContent="A heat pump water heater uses electricity to heat water up to 4x more efficiently than a traditional gas water heater. "
                value={addHeatPumpWaterHeater}
                setValue={(b: boolean) => {
                    setAddHeatPumpWaterHeater(b);
                    ssrTrack(
                        b
                            ? 'clickAddHeatPumpWaterHeater'
                            : 'clickRemoveHeatPumpWaterHeater'
                    );
                }}
                label={
                    addHeatPumpWaterHeater
                        ? 'Heat Pump Water Heater Added'
                        : 'Adding Heat Pump Water Heater?'
                }
            />
        </div>
    );
};

const AddInductionStoveFormBox: React.FC = () => {
    const [addInductionStove, setAddInductionStove] = useAtom(
        addInductionStoveAtom
    );
    const [hasElectricStove] = useAtom(hasElectricStoveAtom);

    const showAddInductionStove = !hasElectricStove;

    useEffect(() => {
        if (!showAddInductionStove) {
            setAddInductionStove(false);
        }
    }, [showAddInductionStove, setAddInductionStove]);

    if (!showAddInductionStove) return null;

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addInductionStoveBox,
                addInductionStove && styles.expanded
            )}
        >
            <BooleanForm
                value={addInductionStove}
                setValue={(b: boolean) => {
                    setAddInductionStove(b);
                    ssrTrack(
                        b
                            ? 'clickAddInductionStove'
                            : 'clickRemoveInductionStove'
                    );
                }}
                tooltipContent="Electric and induction stoves are faster, safer, and more efficient than gas stoves. Plus, they improve indoor air quality — an upgrade that's good for your home and the planet!"
                label={
                    addInductionStove
                        ? 'Electric/Induction Stove Added'
                        : 'Adding an Electric/Induction Stove?'
                }
            />
        </div>
    );
};

const AddPeopleFormBox: React.FC = () => {
    const [changeAddPeople, setChangeAddPeople] = useAtom(changeAddPeopleAtom);
    const [addPeople, setAddPeople] = useAtom(addPeopleAtom);
    const [hhsize, setHhsize] = useAtom(hhSizeAtom);

    // Clear data on collapse.
    useEffect(() => {
        if (!changeAddPeople) {
            setAddPeople('');
        }
    }, [changeAddPeople, setAddPeople, setChangeAddPeople]);

    const changePeopleValues = useMemo(() => {
        // Ensure we don't go below 0 total household size
        // Range from -(hhsize-1) to +5
        const minChange = -(parseInt(hhsize) - 1); // Can't go below 1 person total
        const maxChange = 5;

        const values: string[] = [];

        // Add negative values
        for (let i = minChange; i <= -1; i++) {
            values.push(i.toString());
        }

        // Add positive values
        for (let i = 1; i <= maxChange; i++) {
            values.push(`+${i}`);
        }

        return values.reverse();
    }, [hhsize]);

    return (
        <div
            className={clsx(
                styles.formBox,
                styles.addPeopleBox,
                changeAddPeople && styles.expanded
            )}
        >
            <ExpandableForm
                showAddedLabel={addPeople !== ''}
                expand={changeAddPeople}
                setExpand={(b: boolean) => {
                    setChangeAddPeople(b);
                    ssrTrack(
                        b
                            ? `clickOpenChangeOccupants`
                            : `clickCloseChangeOccupants`
                    );
                }}
                label="Household Size Changes?"
            >
                <FormInputs expand={changeAddPeople}>
                    <Select
                        className={InputStyleClassName}
                        checkIconPosition="right"
                        label="Change in Occupants"
                        value={addPeople}
                        onChange={(value) => {
                            setAddPeople(value ?? '');
                        }}
                        data={changePeopleValues}
                    />
                </FormInputs>
            </ExpandableForm>
        </div>
    );
};
